import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { revalidateUserConfig } from '../store/redux/actions/userActionCreator';

interface RootState {
  user: {
    userId?: string | null;
    userRules?: { shiftSwapConfig?: string };
    company?: { shiftSwapEnabled?: boolean };
  };
}

interface ShiftSwapEnabledRouteProps {
  children: React.ReactElement;
}

// One authoritative company-rule reconcile per page-load session. Once the first mount of the
// guard has refreshed getUserInfo, later client-side navigations to /shift-swaps trust the
// (now-fresh) Redux snapshot without re-fetching or briefly holding render.
let reconciledThisLoad = false;

/**
 * Route guard for /shift-swaps. Redirects home when the shift-swap feature is not enabled for the
 * current user — effective enablement = per-user override (Enabled/Disabled) else the company default,
 * mirroring ShiftSwapService.ResolveShiftSwapEnabled. This is UX-only (prevents a blank/erroring page
 * on direct URL entry); the backend remains the security boundary and rejects disabled users.
 *
 * NAV-3: on the first authenticated load (direct URL / hard reload), the Redux snapshot is rehydrated
 * from localStorage (redux-persist) and can carry a STALE ShiftSwapEnabled — an admin toggled the
 * company rule off while the app was closed. PrivateRoute's getUserInfo refresh is skipped for a
 * returning session, and MasterLayout's one-shot revalidate can resolve AFTER this guard has already
 * admitted the user, so a now-disabled employee still reaches the page. We force an authoritative
 * reconcile on first mount and hold the gate (for a cached "enabled") until it resolves — applying the
 * company rule on first load, not only on a later client-side re-nav.
 */
export const ShiftSwapEnabledRoute = ({ children }: ShiftSwapEnabledRouteProps) => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.user?.userId);
  const shiftSwapConfig = useSelector((state: RootState) => state.user?.userRules?.shiftSwapConfig);
  const companyShiftSwapEnabled = useSelector((state: RootState) => state.user?.company?.shiftSwapEnabled);

  const [reconciled, setReconciled] = useState(reconciledThisLoad);
  useEffect(() => {
    if (reconciledThisLoad || !userId) {
      setReconciled(true);
      return;
    }
    let active = true;
    // redux-thunk's dispatch returns the thunk's own return value (a Promise here); the base
    // Dispatch type doesn't model that, so narrow it through `unknown` rather than `any`.
    const dispatchThunk = dispatch as unknown as (
      thunk: ReturnType<typeof revalidateUserConfig>,
    ) => Promise<void>;
    Promise.resolve(dispatchThunk(revalidateUserConfig())).finally(() => {
      reconciledThisLoad = true;
      if (active) setReconciled(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch, userId]);

  const enabled =
    shiftSwapConfig === 'Enabled' ? true :
    shiftSwapConfig === 'Disabled' ? false :
    !!companyShiftSwapEnabled;

  // A cached "disabled" is safe to act on immediately (the same value gates the nav item, so the
  // user did not reach here via the menu; the fresh value can only keep it disabled). A cached
  // "enabled" may be stale, so wait for the reconcile before admitting on first load.
  if (!enabled) {
    return <Navigate to="/" replace />;
  }
  if (!reconciled) {
    return null;
  }

  return children;
};

export default ShiftSwapEnabledRoute;
